import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Globe } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { LOCALES, LOCALE_FLAG, LOCALE_LABEL, withLocale, type Locale } from "@/i18n/types";
import { useLocale } from "@/i18n/LocaleProvider";

export function LocaleSwitcher({ compact = false }: { compact?: boolean }) {
  const current = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function switchTo(loc: Locale) {
    setOpen(false);
    if (loc === current) return;
    try {
      document.cookie = `kentro-locale=${loc}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    } catch {
      /* noop */
    }
    const target = withLocale(pathname, loc);
    navigate({ to: target, replace: false });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
      >
        <Globe className="size-4" />
        {!compact && <span>{LOCALE_FLAG[current]}</span>}
        <span className="text-xs uppercase tracking-wider">{current}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[150px] overflow-hidden rounded-md border border-white/10 bg-background/95 shadow-lg backdrop-blur-xl"
        >
          {LOCALES.map((loc) => (
            <li key={loc}>
              <button
                role="option"
                aria-selected={loc === current}
                onClick={() => switchTo(loc)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-white/5 ${
                  loc === current ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                <span>{LOCALE_FLAG[loc]}</span>
                <span>{LOCALE_LABEL[loc]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
